import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import {
  EmailLabelRule,
  EmailOAuthAccount,
  GetEmailLabelRulesResponse,
  CreateEmailLabelRuleRequest,
  CreateEmailLabelRuleResponse,
  UpdateEmailLabelRuleRequest,
  UpdateEmailLabelRuleResponse,
  DeleteEmailLabelRuleResponse,
  ToggleEmailLabellingResponse,
} from '@dreamai/types';
import api from '../../../api';
import { AppTab } from './appLayoutConfig';
import './inbound-email-wrapper.scss';

interface InboundEmailWrapperProps {
  threadId?: string;
  tabId?: string;
  onTabsReady?: (tabs: AppTab[]) => void;
}

/** Known built-in classification keys with display names and backend defaults */
const BUILT_IN_CLASSIFICATIONS: {
  key: string;
  label: string;
  defaultLabelName: string;
  defaultColor: { bg: string; text: string };
  defaultArchive: boolean;
}[] = [
  {
    key: 'spam',
    label: 'Spam',
    defaultLabelName: 'Junk',
    defaultColor: { bg: '#f691b2', text: '#711a36' },
    defaultArchive: true,
  },
  {
    key: 'promotional',
    label: 'Promotional',
    defaultLabelName: 'Promotional',
    defaultColor: { bg: '#fad165', text: '#594c05' },
    defaultArchive: true,
  },
  {
    key: 'receipt',
    label: 'Receipt',
    defaultLabelName: 'Receipt',
    defaultColor: { bg: '#a4c2f4', text: '#0d3472' },
    defaultArchive: true,
  },
  {
    key: 'calendar_invite',
    label: 'Calendar Invite',
    defaultLabelName: 'Calendar',
    defaultColor: { bg: '#b3efd3', text: '#0b4f30' },
    defaultArchive: false,
  },
  {
    key: 'booking_confirmation',
    label: 'Booking Confirmation',
    defaultLabelName: 'Receipt',
    defaultColor: { bg: '#a4c2f4', text: '#0d3472' },
    defaultArchive: false,
  },
];

const INBOUND_EMAIL_TABS: AppTab[] = [
  { id: 'email-tagging', label: 'Email Tagging' },
];

/** Gmail system/reserved label names that cannot be used for user labels */
const RESERVED_GMAIL_LABEL_NAMES = new Set(
  [
    'important',
    'starred',
    'spam',
    'trash',
    'drafts',
    'sent',
    'inbox',
    'unread',
    'chat',
    'category_personal',
    'category_social',
    'category_promotions',
    'category_updates',
    'category_forums',
  ].map((s) => s.toLowerCase()),
);

function isReservedLabelName(name: string): boolean {
  return RESERVED_GMAIL_LABEL_NAMES.has(name.trim().toLowerCase());
}

/**
 * Gmail pre-approved label colour pairs.
 * All values verified against the Gmail API v1 predefined color set:
 * https://developers.google.com/resources/api-libraries/documentation/gmail/v1/python/latest/gmail_v1.users.labels.html
 */
const GMAIL_LABEL_COLORS: { bg: string; text: string }[] = [
  { bg: '#fb4c2f', text: '#ffffff' },
  { bg: '#e66550', text: '#ffffff' },
  { bg: '#efa093', text: '#000000' },
  { bg: '#ff7537', text: '#ffffff' },
  { bg: '#ffad46', text: '#ffffff' },
  { bg: '#fad165', text: '#594c05' },
  { bg: '#fbe983', text: '#594c05' },
  { bg: '#16a766', text: '#ffffff' },
  { bg: '#43d692', text: '#ffffff' },
  { bg: '#b3efd3', text: '#0b4f30' },
  { bg: '#2da2bb', text: '#ffffff' },
  { bg: '#98d7e4', text: '#0d3b44' },
  { bg: '#4a86e8', text: '#ffffff' },
  { bg: '#a4c2f4', text: '#0d3472' },
  { bg: '#a479e2', text: '#ffffff' },
  { bg: '#b99aff', text: '#ffffff' },
  { bg: '#e3d7ff', text: '#3d188e' },
  { bg: '#f691b2', text: '#711a36' },
  { bg: '#fcdee8', text: '#711a36' },
  { bg: '#cca6ac', text: '#662e37' },
  { bg: '#ebdbde', text: '#662e37' },
];

const InboundEmailWrapper: React.FC<InboundEmailWrapperProps> = ({
  threadId,
  tabId = 'email-tagging',
  onTabsReady,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [rules, setRules] = useState<EmailLabelRule[]>([]);
  const [accounts, setAccounts] = useState<EmailOAuthAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingOauthId, setTogglingOauthId] = useState<number | null>(null);
  const lastTabsRef = useRef<string>('');

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRule, setEditingRule] = useState<EmailLabelRule | null>(null);

  const fetchRules = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<GetEmailLabelRulesResponse>(
        '/email-label-rules',
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        setRules(response.data.rules);
        setAccounts(response.data.accounts);
      } else {
        setError('Failed to load email label rules');
      }
    } catch (err) {
      console.error('Error fetching email label rules:', err);
      setError('Failed to load email label rules');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  // Notify parent of tabs
  useEffect(() => {
    if (loading || !onTabsReady) return;

    const tabs = INBOUND_EMAIL_TABS;
    const tabsKey = JSON.stringify(tabs);

    if (tabsKey !== lastTabsRef.current) {
      lastTabsRef.current = tabsKey;
      onTabsReady(tabs);
    }
  }, [loading, onTabsReady]);

  const handleToggleLabelling = async (account: EmailOAuthAccount) => {
    if (togglingOauthId !== null) return;
    setTogglingOauthId(account.oauthId);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.patch<ToggleEmailLabellingResponse>(
        '/email-label-rules/toggle-labelling',
        { oauthId: account.oauthId, enabled: !account.emailLabelling },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        setAccounts((prev) =>
          prev.map((a) =>
            a.oauthId === response.data.oauthId
              ? { ...a, emailLabelling: response.data.emailLabellingEnabled }
              : a,
          ),
        );
      }
    } catch (err) {
      console.error('Error toggling email labelling:', err);
    } finally {
      setTogglingOauthId(null);
    }
  };

  const handleCreateRule = async (body: CreateEmailLabelRuleRequest) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post<CreateEmailLabelRuleResponse>(
        '/email-label-rules',
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success && response.data.rule) {
        setRules((prev) => [...prev, response.data.rule!]);
        setShowCreateModal(false);
      }
    } catch (err) {
      console.error('Error creating rule:', err);
    }
  };

  const handleUpdateRule = async (
    ruleId: string,
    body: UpdateEmailLabelRuleRequest,
  ) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.patch<UpdateEmailLabelRuleResponse>(
        `/email-label-rules/${ruleId}`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success && response.data.rule) {
        setRules((prev) =>
          prev.map((r) => (r.id === ruleId ? response.data.rule! : r)),
        );
        setEditingRule(null);
      }
    } catch (err) {
      console.error('Error updating rule:', err);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.delete<DeleteEmailLabelRuleResponse>(
        `/email-label-rules/${ruleId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        setRules((prev) => prev.filter((r) => r.id !== ruleId));
      }
    } catch (err) {
      console.error('Error deleting rule:', err);
    }
  };

  // Split rules into built-in (classification-based) and custom (description-based)
  const builtInRules = rules.filter((r) => r.classification !== null);
  const customRules = rules.filter((r) => r.description !== null);

  // Build display list: all built-in classifications (with user override if exists) + custom rules
  const builtInDisplay = BUILT_IN_CLASSIFICATIONS.map((bc) => {
    const userRule = builtInRules.find((r) => r.classification === bc.key);
    return { ...bc, rule: userRule };
  });

  // Rules/defaults that are set to auto-archive
  const archivedItems: {
    key: string;
    labelName: string;
    labelColor: { backgroundColor: string; textColor: string } | null;
    rule: EmailLabelRule | undefined;
  }[] = [];

  // Add built-in classifications that are archived (user override or default)
  for (const bc of BUILT_IN_CLASSIFICATIONS) {
    const userRule = builtInRules.find((r) => r.classification === bc.key);
    const isArchived = userRule ? userRule.archive : bc.defaultArchive;
    if (isArchived) {
      archivedItems.push({
        key: bc.key,
        labelName: userRule?.labelName ?? bc.defaultLabelName,
        labelColor: userRule?.labelColor ?? {
          backgroundColor: bc.defaultColor.bg,
          textColor: bc.defaultColor.text,
        },
        rule: userRule,
      });
    }
  }

  // Add custom rules that are archived
  for (const rule of customRules) {
    if (rule.archive) {
      archivedItems.push({
        key: rule.id,
        labelName: rule.labelName,
        labelColor: rule.labelColor,
        rule,
      });
    }
  }

  if (loading) {
    return <div className='inbound-email-wrapper loading'></div>;
  }

  if (error) {
    return null;
  }

  return (
    <div className='inbound-email-wrapper'>
      <div className='email-tagging-header'>
        <div className='header-text'>
          <h1>Email tagging</h1>
          <p>Your Assistant will keep your inbox tidy</p>
        </div>
      </div>

      {accounts.length > 0 && (
        <div className='labelling-toggles'>
          {accounts.map((account) => (
            <div key={account.oauthId} className='labelling-toggle'>
              <div className='toggle-info'>
                <span className='toggle-label'>
                  Automatically tag incoming emails to
                </span>
                <span className='toggle-email'>{account.email}</span>
              </div>
              <button
                className={`toggle-switch ${account.emailLabelling ? 'on' : 'off'}`}
                onClick={() => handleToggleLabelling(account)}
                disabled={togglingOauthId !== null}
                aria-label={`Toggle email labelling for ${account.email}`}
              >
                <span className='toggle-thumb' />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className='rules-section'>
        <div className='section-header'>
          <h2>Rules</h2>
          <button
            className='add-rule-button'
            onClick={() => setShowCreateModal(true)}
          >
            <span>Add Rule</span>
          </button>
        </div>

        <div className='rules-list'>
          {builtInDisplay.map((item) => (
            <div
              key={item.key}
              className='rule-row'
              style={
                {
                  '--rule-bg':
                    item.rule?.labelColor?.backgroundColor ??
                    item.defaultColor.bg,
                  '--rule-text':
                    item.rule?.labelColor?.textColor ?? item.defaultColor.text,
                } as React.CSSProperties
              }
            >
              <div className='rule-info'>
                <span className='rule-description-label'>
                  Email description
                </span>
                <span className='rule-classification'>{item.label}</span>
              </div>
              <div className='rule-tag'>
                <span className='rule-tag-label'>Tag with</span>
                {item.rule ? (
                  <button
                    className='tag-pill'
                    onClick={() => setEditingRule(item.rule!)}
                  >
                    {item.rule.labelName}
                  </button>
                ) : (
                  <button
                    className='tag-pill unset'
                    onClick={() =>
                      handleCreateRule({
                        classification: item.key,
                        labelName: item.defaultLabelName,
                      })
                    }
                  >
                    {item.defaultLabelName}
                  </button>
                )}
              </div>
            </div>
          ))}

          {customRules.map((rule) => (
            <div
              key={rule.id}
              className='rule-row custom'
              style={
                rule.labelColor
                  ? ({
                      '--rule-bg': rule.labelColor.backgroundColor,
                      '--rule-text': rule.labelColor.textColor,
                    } as React.CSSProperties)
                  : undefined
              }
            >
              <div className='rule-info'>
                <span className='rule-description-label'>
                  Email description
                </span>
                <span className='rule-classification'>{rule.description}</span>
              </div>
              <div className='rule-tag'>
                <span className='rule-tag-label'>Tag with</span>
                <button
                  className='tag-pill'
                  onClick={() => setEditingRule(rule)}
                >
                  {rule.labelName}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className='archive-section'>
        <div className='section-header'>
          <h2>Auto-archive these tags</h2>
        </div>

        <div className='archive-list'>
          {archivedItems.length === 0 ? (
            <p className='empty-state'>
              No tags are set to auto-archive. Edit a rule to enable archiving.
            </p>
          ) : (
            archivedItems.map((item) => (
              <div key={item.key} className='archive-item'>
                <button
                  className='tag-pill'
                  style={
                    item.labelColor
                      ? {
                          backgroundColor: item.labelColor.backgroundColor,
                          color: item.labelColor.textColor,
                        }
                      : undefined
                  }
                  onClick={() => item.rule && setEditingRule(item.rule)}
                >
                  {item.labelName}
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Rule Modal */}
      {showCreateModal && (
        <CreateRuleModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateRule}
        />
      )}

      {/* Edit Rule Modal */}
      {editingRule && (
        <EditRuleModal
          rule={editingRule}
          onClose={() => setEditingRule(null)}
          onSave={(body) => handleUpdateRule(editingRule.id, body)}
          onDelete={() => handleDeleteRule(editingRule.id)}
        />
      )}
    </div>
  );
};

// ── Create Rule Modal ────────────────────────────────────────────────

interface CreateRuleModalProps {
  onClose: () => void;
  onCreate: (body: CreateEmailLabelRuleRequest) => void;
}

const CreateRuleModal: React.FC<CreateRuleModalProps> = ({
  onClose,
  onCreate,
}) => {
  const [description, setDescription] = useState('');
  const [labelName, setLabelName] = useState('');
  const [selectedColor, setSelectedColor] = useState(GMAIL_LABEL_COLORS[0]);
  const [colourPickerOpen, setColourPickerOpen] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim() || !labelName.trim()) return;
    onCreate({
      description: description.trim(),
      labelName: labelName.trim(),
      labelColor: {
        backgroundColor: selectedColor.bg,
        textColor: selectedColor.text,
      },
    });
  };

  return (
    <Modal show onHide={onClose} centered dialogClassName='rule-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Add Rule</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form id='create-rule-form' onSubmit={handleSubmit}>
          <div className='form-group'>
            <label htmlFor='create-description'>
              What sort of emails does this affect
            </label>
            <span className='form-hint'>
              You can specify email addresses, or describe the sender or
              contents
            </span>
            <textarea
              id='create-description'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder='e.g. Emails from suppliers with invoices attached'
              rows={3}
            />
          </div>
          <div className='form-group'>
            <label htmlFor='create-label'>What tag should be applied</label>
            <input
              id='create-label'
              type='text'
              value={labelName}
              onChange={(e) => setLabelName(e.target.value)}
              placeholder='e.g. Supplier Invoices'
            />
            {isReservedLabelName(labelName) && (
              <span className='form-error'>
                &ldquo;{labelName.trim()}&rdquo; can't be used as it is a
                reserved Google label name. Please choose a different name.
              </span>
            )}
          </div>
          <div className='form-group'>
            <label>Tag colour</label>
            <div className='colour-picker'>
              <button
                type='button'
                className='colour-picker-trigger'
                style={{
                  backgroundColor: selectedColor.bg,
                  color: selectedColor.text,
                }}
                onClick={() => setColourPickerOpen(!colourPickerOpen)}
              >
                {labelName.trim() || '\u00A0'}
              </button>
              {colourPickerOpen && (
                <div className='colour-picker-dropdown'>
                  {GMAIL_LABEL_COLORS.map((c) => (
                    <button
                      key={c.bg}
                      type='button'
                      className={`colour-picker-option ${selectedColor.bg === c.bg ? 'selected' : ''}`}
                      style={{ backgroundColor: c.bg, color: c.text }}
                      onClick={() => {
                        setSelectedColor(c);
                        setColourPickerOpen(false);
                      }}
                    >
                      {labelName.trim() || '\u00A0'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button type='button' onClick={onClose}>
          Cancel
        </button>
        <button
          type='submit'
          form='create-rule-form'
          className='action-btn'
          disabled={
            !description.trim() ||
            !labelName.trim() ||
            isReservedLabelName(labelName)
          }
        >
          Save Rule
        </button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Edit Rule Modal ──────────────────────────────────────────────────

interface EditRuleModalProps {
  rule: EmailLabelRule;
  onClose: () => void;
  onSave: (body: UpdateEmailLabelRuleRequest) => void;
  onDelete: () => void;
}

const EditRuleModal: React.FC<EditRuleModalProps> = ({
  rule,
  onClose,
  onSave,
  onDelete,
}) => {
  const [labelName, setLabelName] = useState(rule.labelName);
  const [archive, setArchive] = useState(rule.archive);
  const [description, setDescription] = useState(rule.description ?? '');
  const [selectedColor, setSelectedColor] = useState(
    GMAIL_LABEL_COLORS.find((c) => c.bg === rule.labelColor?.backgroundColor) ??
      GMAIL_LABEL_COLORS[0],
  );
  const [colourPickerOpen, setColourPickerOpen] = useState(false);
  const isCustom = rule.classification === null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!labelName.trim()) return;
    const body: UpdateEmailLabelRuleRequest = {
      labelName: labelName.trim(),
      archive,
      labelColor: {
        backgroundColor: selectedColor.bg,
        textColor: selectedColor.text,
      },
    };
    if (isCustom) {
      body.description = description.trim() || null;
    }
    onSave(body);
  };

  return (
    <Modal show onHide={onClose} centered dialogClassName='rule-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Edit Rule</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form id='edit-rule-form' onSubmit={handleSubmit}>
          {isCustom && (
            <div className='form-group'>
              <label htmlFor='edit-description'>
                What sort of emails does this affect
              </label>
              <span className='form-hint'>
                You can specify email addresses, or describe the sender or
                contents
              </span>
              <textarea
                id='edit-description'
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
          )}
          <div className='form-group'>
            <label htmlFor='edit-label'>What tag should be applied</label>
            <input
              id='edit-label'
              type='text'
              value={labelName}
              onChange={(e) => setLabelName(e.target.value)}
            />
            {isReservedLabelName(labelName) && (
              <span className='form-error'>
                &ldquo;{labelName.trim()}&rdquo; is a reserved Gmail label name.
                Please choose a different name.
              </span>
            )}
          </div>
          <div className='form-group'>
            <label>Tag colour</label>
            <div className='colour-picker'>
              <button
                type='button'
                className='colour-picker-trigger'
                style={{
                  backgroundColor: selectedColor.bg,
                  color: selectedColor.text,
                }}
                onClick={() => setColourPickerOpen(!colourPickerOpen)}
              >
                {labelName.trim() || '\u00A0'}
              </button>
              {colourPickerOpen && (
                <div className='colour-picker-dropdown'>
                  {GMAIL_LABEL_COLORS.map((c) => (
                    <button
                      key={c.bg}
                      type='button'
                      className={`colour-picker-option ${selectedColor.bg === c.bg ? 'selected' : ''}`}
                      style={{ backgroundColor: c.bg, color: c.text }}
                      onClick={() => {
                        setSelectedColor(c);
                        setColourPickerOpen(false);
                      }}
                    >
                      {labelName.trim() || '\u00A0'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <label className='checkbox-label'>
            <input
              type='checkbox'
              checked={archive}
              onChange={(e) => setArchive(e.target.checked)}
            />
            Auto-archive emails with this tag
          </label>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='delete-button' onClick={onDelete}>
          Delete
        </button>
        <div className='action-buttons'>
          <button type='button' onClick={onClose}>
            Cancel
          </button>
          <button
            type='submit'
            form='edit-rule-form'
            className='action-btn'
            disabled={!labelName.trim() || isReservedLabelName(labelName)}
          >
            Save Rule
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default InboundEmailWrapper;
